import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import type { MeApiResponse } from "@repo/ping-react-js";
import { Button } from "@repo/ping-design";

import { useUpdateAnalyticsConsentMutation } from "services/appSlice";
import {
  ANALYTICS_CONSENT_VERSION,
  applyPostHogConsent,
  getHydratedAnonymousDecision,
  isAnalyticsConsentCurrent,
  OPEN_ANALYTICS_CONSENT_EVENT,
  readStoredAnalyticsConsent,
  storeAnalyticsConsent,
  suspendPostHogConsent,
  type AnalyticsConsentDecision,
  type StoredAnalyticsConsent,
} from "utils/analyticsConsent";

import "./AnalyticsConsentManager.scss";

const COOKIE_POLICY_URL = "https://www.pingintel.com/documents/ping-data-cookie-policy.pdf";

type AnalyticsConsentManagerProps = {
  accessToken?: string;
  consentSubject?: string;
  isLoggedIn: boolean;
  isImpersonating?: boolean;
  isMeLoading: boolean;
  me?: MeApiResponse;
  onConsentApplied: (decision: AnalyticsConsentDecision) => void;
};

const getServerDecision = (me?: MeApiResponse): AnalyticsConsentDecision | null => {
  if (
    me?.analytics_consent_version !== ANALYTICS_CONSENT_VERSION ||
    typeof me.analytics_consent !== "boolean" ||
    !isAnalyticsConsentCurrent(me.analytics_consent_updated_at)
  ) {
    return null;
  }

  return me.analytics_consent ? "accepted" : "rejected";
};

const AnalyticsConsentManager = ({
  accessToken,
  consentSubject,
  isLoggedIn,
  isImpersonating = false,
  isMeLoading,
  me,
  onConsentApplied,
}: AnalyticsConsentManagerProps) => {
  const posthog = usePostHog();
  const [updateAnalyticsConsent, { isLoading: isSaving }] = useUpdateAnalyticsConsentMutation();
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const appliedDecision = useRef<AnalyticsConsentDecision | null>(null);
  const attemptedSync = useRef<string | null>(null);
  const requestGeneration = useRef(0);
  const impersonating = isImpersonating || !!me?.impersonator_user_id;
  const waitingForAccount = isLoggedIn && (isMeLoading || !me || !accessToken || !consentSubject);
  const subject = isLoggedIn ? consentSubject : undefined;
  const consentContext = isLoggedIn ? `account:${subject}` : "anonymous";

  useLayoutEffect(() => {
    const suspendAnalytics = () => {
      if (posthog) suspendPostHogConsent(posthog);
      requestGeneration.current += 1;
    };

    // Clear identity before child effects can capture events for a different user.
    suspendAnalytics();
    appliedDecision.current = null;
    attemptedSync.current = null;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset the banner with the external analytics context before paint.
    setIsVisible(false);
    setError(null);
    return suspendAnalytics;
  }, [consentContext, impersonating, posthog, waitingForAccount]);

  useEffect(() => {
    const handleOpenConsentManager = () => {
      if (impersonating || waitingForAccount) return;
      setError(null);
      setIsVisible(true);
    };

    window.addEventListener(OPEN_ANALYTICS_CONSENT_EVENT, handleOpenConsentManager);
    return () => window.removeEventListener(OPEN_ANALYTICS_CONSENT_EVENT, handleOpenConsentManager);
  }, [impersonating, waitingForAccount]);

  const applyDecision = useCallback(
    (decision: AnalyticsConsentDecision, alreadyHydrated = false) => {
      if (!posthog || appliedDecision.current === decision) return;
      appliedDecision.current = decision;
      applyPostHogConsent(posthog, decision, false);
      onConsentApplied(decision);
      if (!alreadyHydrated) posthog.capture("$pageview");
      setIsVisible(false);
    },
    [onConsentApplied, posthog],
  );

  const persistForUser = useCallback(
    async (consent: StoredAnalyticsConsent) => {
      if (!isLoggedIn || waitingForAccount || impersonating) return;
      attemptedSync.current = `${consent.decision}:${consent.updatedAt}`;
      const generation = ++requestGeneration.current;

      try {
        const response = await updateAnalyticsConsent({
          analyticsConsent: consent.decision === "accepted",
          accessToken: accessToken!,
        }).unwrap();
        if (generation !== requestGeneration.current) return;

        storeAnalyticsConsent(consent.decision, subject, {
          updatedAt: response.analytics_consent_updated_at,
          pendingSync: false,
        });
        setError(null);
        setIsVisible(false);
      } catch {
        if (generation !== requestGeneration.current) return;
        setError("Your choice is active on this browser, but we could not save it to your account. Please try again.");
        setIsVisible(true);
      }
    },
    [accessToken, impersonating, isLoggedIn, subject, updateAnalyticsConsent, waitingForAccount],
  );

  useEffect(() => {
    if (!posthog || impersonating || waitingForAccount) return;

    const serverDecision = isLoggedIn ? getServerDecision(me) : null;
    const storedConsent = readStoredAnalyticsConsent();
    const storedDecisionApplies = !!storedConsent && (!storedConsent.subject || storedConsent.subject === subject);
    const hasPendingDecision = storedDecisionApplies && (storedConsent?.pendingSync || !storedConsent?.subject);
    // /me can still contain the pre-PATCH value while its cache refreshes.
    const hasNewerSavedDecision =
      storedDecisionApplies &&
      storedConsent.pendingSync === false &&
      Date.parse(storedConsent.updatedAt) > Date.parse(me?.analytics_consent_updated_at ?? "");

    // A browser's anonymous choice must not replace an account's existing preference.
    if (serverDecision && (!storedConsent?.subject || (!hasPendingDecision && !hasNewerSavedDecision))) {
      storeAnalyticsConsent(serverDecision, subject, {
        updatedAt: me!.analytics_consent_updated_at!,
        pendingSync: false,
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Keep the banner in sync with server and local consent.
      applyDecision(serverDecision);
      return;
    }

    if (storedDecisionApplies && storedConsent) {
      const alreadyHydrated = !isLoggedIn && getHydratedAnonymousDecision() === storedConsent.decision;
      applyDecision(storedConsent.decision, alreadyHydrated);

      if (
        isLoggedIn &&
        hasPendingDecision &&
        attemptedSync.current !== `${storedConsent.decision}:${storedConsent.updatedAt}`
      ) {
        // Bind an anonymous choice before starting the request, including when it fails.
        const pendingConsent = storeAnalyticsConsent(storedConsent.decision, subject, {
          updatedAt: storedConsent.updatedAt,
          pendingSync: true,
        });
        void persistForUser(pendingConsent);
      }
      return;
    }

    if (appliedDecision.current !== null) {
      suspendPostHogConsent(posthog);
      appliedDecision.current = null;
    }
    setIsVisible(true);
  }, [applyDecision, impersonating, isLoggedIn, me, persistForUser, posthog, subject, waitingForAccount]);

  const handleDecision = async (decision: AnalyticsConsentDecision) => {
    if (!posthog || impersonating || waitingForAccount || isSaving) return;

    setError(null);
    const storedConsent = storeAnalyticsConsent(decision, subject);
    applyDecision(decision);
    setIsVisible(false);
    await persistForUser(storedConsent);
  };

  if (!isVisible || impersonating || waitingForAccount) return null;

  return (
    <aside
      className="AnalyticsConsentManager"
      role="region"
      aria-label="Cookie consent"
      aria-live="polite"
      aria-busy={isSaving}
    >
      <div className="AnalyticsConsentManager__copy">
        <h2>Allow analytics cookies?</h2>
        <p>
          Optional analytics cookies help us understand which features you use to improve Ping.Location. If you decline, we only count visits in aggregate, without cookies. Cookies required for sign-in and security are always enabled and are not affected by this choice. {" "}
          <a href={COOKIE_POLICY_URL} target="_blank" rel="noreferrer">
            Cookie Policy
          </a>
        </p>
        {error && (
          <p className="AnalyticsConsentManager__error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="AnalyticsConsentManager__actions">
        <Button
          type="button"
          className="AnalyticsConsentManager__button"
          variant="outline"
          size="md"
          label="Decline"
          disabled={isSaving}
          onClick={() => void handleDecision("rejected")}
        />
        <Button
          type="button"
          className="AnalyticsConsentManager__button"
          variant="solid"
          size="md"
          label="Allow"
          disabled={isSaving}
          onClick={() => void handleDecision("accepted")}
        />
      </div>
    </aside>
  );
};

export default AnalyticsConsentManager;
